import React from 'react'
import GradientButton from '../../components/GradientButton'
import LotStoryLibraryDetailPanel from './LotStoryLibraryDetailPanel'
import type {
  LotStoryLibraryDetailActions,
  LotStoryLibraryDetailState,
  LotStoryLibraryPanelActions,
  LotStoryLibraryPanelState,
  TranslateFn
} from './lotStoryLibraryTypes'
import {
  getStoryFamilyLabel,
  getStoryFamilyTechnicalId,
  type LotStoryLibraryPresentation
} from './lotStoryPresentation'
import {
  getLotStoryArtifactStatusBadgeStyle,
  getLotStoryArtifactStatusLabel
} from './lotStoryLibraryUi'

type LotStoryLibraryPanelProps = {
  t: TranslateFn
  libraryState: LotStoryLibraryPanelState
  libraryActions: LotStoryLibraryPanelActions
  presentation: LotStoryLibraryPresentation
}

export default function LotStoryLibraryPanel({
  t,
  libraryState,
  libraryActions,
  presentation
}: Readonly<LotStoryLibraryPanelProps>) {
  const {
    storyLibraryLoading,
    selectedStoryArtifactLoading,
    storyLibraryError,
    selectedStoryArtifactError,
    storyLibrary,
    selectedStoryArtifact,
    selectedStoryPreviewVersionId,
    canDraftLotStories,
    canRevokeLotStories,
    storyPrivateWritePreflight,
    storyGroupReadPreflight,
    storyGroupDelivery,
    storyGroupDeliveryRenderedReceipt,
    storyGroupShareReviewPreflight,
    storyGroupShareShell,
    storyPublishing,
    storyRevoking
  } = libraryState
  const {
    onRefreshLibrary,
    onSelectArtifactId,
    onOpenArtifact,
    onOpenArtifactVersion,
    onSelectGroupReadPreflightGroup,
    onRecordGroupShareReview,
    onRecordGroupShareApproval,
    onRecordGroupShareReleaseGate,
    onPromoteStoryToGroup,
    onSaveGroupShareIntent,
    onPublishArtifact,
    onRevokeArtifact
  } = libraryActions
  const [activeArtifactId, setActiveArtifactId] = React.useState<string | null>(selectedStoryArtifact?.artifact.artifactId ?? null)
  const [isArtifactSwitcherOpen, setIsArtifactSwitcherOpen] = React.useState(!selectedStoryArtifact)
  const lastAutoCollapsedArtifactIdRef = React.useRef<string | null>(selectedStoryArtifact?.artifact.artifactId ?? null)
  const detailState: LotStoryLibraryDetailState | null = selectedStoryArtifact
    ? {
        selectedStoryArtifact,
        selectedStoryPreviewVersionId,
        canDraftLotStories,
        canRevokeLotStories,
        storyPrivateWritePreflight,
        storyGroupReadPreflight,
        storyGroupDelivery,
        storyGroupDeliveryRenderedReceipt,
        storyGroupShareReviewPreflight,
        storyGroupShareShell,
        storyPublishing,
        storyRevoking
      }
    : null
  const detailActions: LotStoryLibraryDetailActions = {
    onOpenArtifact,
    onOpenArtifactVersion,
    onSelectGroupReadPreflightGroup,
    onRecordGroupShareReview,
    onRecordGroupShareApproval,
    onRecordGroupShareReleaseGate,
    onPromoteStoryToGroup,
    onSaveGroupShareIntent,
    onPublishArtifact,
    onRevokeArtifact
  }

  React.useEffect(() => {
    if (selectedStoryArtifactLoading) {
      return
    }

    const selectedArtifactId = selectedStoryArtifact?.artifact.artifactId ?? null
    if (selectedArtifactId) {
      if (activeArtifactId !== selectedArtifactId) {
        setActiveArtifactId(selectedArtifactId)
      }
      return
    }

    if (storyLibrary.length === 0) {
      if (activeArtifactId !== null) {
        setActiveArtifactId(null)
      }
      return
    }

    if (activeArtifactId && storyLibrary.some((artifact) => artifact.artifact.artifactId === activeArtifactId)) {
      return
    }

    setActiveArtifactId(storyLibrary[0].artifact.artifactId)
  }, [activeArtifactId, selectedStoryArtifact, selectedStoryArtifactLoading, storyLibrary])

  React.useEffect(() => {
    const selectedArtifactId = selectedStoryArtifact?.artifact.artifactId ?? null

    if (!selectedArtifactId) {
      lastAutoCollapsedArtifactIdRef.current = null
      return
    }

    if (storyLibrary.length <= 1) {
      lastAutoCollapsedArtifactIdRef.current = selectedArtifactId
      if (isArtifactSwitcherOpen) {
        setIsArtifactSwitcherOpen(false)
      }
      return
    }

    if (lastAutoCollapsedArtifactIdRef.current !== selectedArtifactId) {
      lastAutoCollapsedArtifactIdRef.current = selectedArtifactId
      setIsArtifactSwitcherOpen(false)
    }
  }, [isArtifactSwitcherOpen, selectedStoryArtifact, storyLibrary.length])

  const activeArtifactSummary = storyLibrary.find((artifact) => artifact.artifact.artifactId === activeArtifactId)
    ?? selectedStoryArtifact
    ?? null
  const activeArtifactFamilyLabel = activeArtifactSummary
    ? getStoryFamilyLabel(activeArtifactSummary.version.projectionSnapshot)
    : ''
  const activeArtifactFamilyTechnicalId = activeArtifactSummary
    ? getStoryFamilyTechnicalId(activeArtifactSummary.version.projectionSnapshot)
    : ''

  const handleArtifactSelection = (artifactId: string) => {
    setActiveArtifactId(artifactId)
    setIsArtifactSwitcherOpen(false)
    onSelectArtifactId(artifactId)
  }

  return (
    <section style={{ border: '1px solid #dbeafe', borderRadius: 12, backgroundColor: '#f8fbff', padding: 16, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0' }}>{t('lots.story.library.title')}</h3>
          <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{t('lots.story.library.subtitle')}</p>
        </div>
        <GradientButton
          type="button"
          onClick={onRefreshLibrary}
          disabled={storyLibraryLoading || selectedStoryArtifactLoading}
        >
          {storyLibraryLoading ? t('lots.story.library.refreshing') : t('lots.story.library.refresh')}
        </GradientButton>
      </div>

      {storyLibraryError ? (
        <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
          {storyLibraryError}
        </div>
      ) : null}

      {storyLibraryLoading ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.story.library.loading')}</div>
      ) : storyLibrary.length === 0 ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.story.library.empty')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ border: '1px solid #dbeafe', borderRadius: 12, backgroundColor: '#ffffff', padding: 12, display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>{t('lots.story.library.switcher.title')}</strong>
                  <div style={{ fontSize: 12, color: '#475569' }}>{t('lots.story.library.switcher.subtitle')}</div>
                </div>
                {storyLibrary.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => setIsArtifactSwitcherOpen((current) => !current)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      color: '#334155',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {isArtifactSwitcherOpen ? t('lots.story.library.switcher.close') : t('lots.story.library.switcher.open')}
                  </button>
                ) : null}
              </div>

              {activeArtifactSummary ? (
                <div style={{ display: 'grid', gap: 4, padding: 10, borderRadius: 10, border: '1px solid #dbeafe', backgroundColor: '#eff6ff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13, color: '#111827' }}>{activeArtifactSummary.version.title}</strong>
                    <span style={{ ...getLotStoryArtifactStatusBadgeStyle(activeArtifactSummary.artifact.status), padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      {getLotStoryArtifactStatusLabel(t, activeArtifactSummary.artifact.status)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#1d4ed8' }}>
                    {t('lots.story.library.item.version', { version: activeArtifactSummary.version.versionNumber })}
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', wordBreak: 'break-word' }}>{activeArtifactFamilyLabel}</div>
                  {activeArtifactFamilyTechnicalId ? (
                    <div style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-word' }}>{activeArtifactFamilyTechnicalId}</div>
                  ) : null}
                </div>
              ) : null}

              {isArtifactSwitcherOpen ? (
                <div style={{ display: 'grid', gap: 8, padding: 10, borderRadius: 10, border: '1px solid #dbeafe', backgroundColor: '#f8fbff' }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{t('lots.story.library.switcher.field')}</span>
                    <select
                      value={activeArtifactId ?? ''}
                      onChange={(event) => {
                        if (!event.target.value) {
                          return
                        }
                        handleArtifactSelection(event.target.value)
                      }}
                      style={{
                        width: '100%',
                        borderRadius: 10,
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        padding: '10px 12px',
                        fontSize: 13
                      }}
                    >
                      {storyLibrary.map((artifact) => (
                        <option key={artifact.artifact.artifactId} value={artifact.artifact.artifactId}>
                          {`${artifact.version.title} · ${t('lots.story.library.item.version', { version: artifact.version.versionNumber })}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{t('lots.story.library.switcher.help')}</div>
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ border: '1px solid #dbeafe', borderRadius: 12, backgroundColor: '#ffffff', padding: 14, display: 'grid', gap: 12 }}>
            {selectedStoryArtifactLoading ? (
              <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.story.library.loading')}</div>
            ) : selectedStoryArtifactError ? (
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
                {selectedStoryArtifactError}
              </div>
            ) : !selectedStoryArtifact ? (
              <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.story.library.detail.empty')}</div>
            ) : (
              detailState ? (
                <LotStoryLibraryDetailPanel
                  t={t}
                  detailState={detailState}
                  detailActions={detailActions}
                  presentation={presentation}
                />
              ) : null
            )}
          </div>
        </div>
      )}
    </section>
  )
}